import { Box, Button, List, Text } from '@chakra-ui/react';
import RightArrow from 'src/assets/icons/RightArrow';
import { useSubscriptionCache } from 'src/apollo/cache';
import assert from 'src/utils/assert';
import getPrettyDate from 'src/utils/getPrettyDate';
import { ShowsDocument, useRemoveShowMutation, useShowsQuery } from 'src/generated/graphql';
import SubscriptionLockedNotice from 'src/pages/my-account/components/SubscriptionLockedNotice';
import useSubscriptionManagementLock from 'src/pages/my-account/utils/useSubscriptionManagementLock';
import SupportLink from '../intercom/SupportLink';
import { useMyShowsContext } from './MyShowsContext';

export default function RemoveShow() {
  const { showToRemove, setShowToRemove, setContentType } = useMyShowsContext();
  assert(showToRemove, 'showToRemove not found in context, expected a show id');
  const [removeShow, { loading }] = useRemoveShowMutation({
    variables: {
      id: showToRemove,
    },
    onCompleted: () => {
      setShowToRemove(null);
      setContentType('show-removed-confirmation');
    },
    refetchQueries: [ShowsDocument],
  });
  const { data } = useShowsQuery({ fetchPolicy: 'cache-only' });
  const showExpiresAt = data?.shows.find(s => s.id === showToRemove)?.showExpiresAt;
  const subscription = useSubscriptionCache();
  const { locked, reason } = useSubscriptionManagementLock(subscription);

  // Locked for PayPal-on-source customers (can't self-manage) and during the
  // payment-migration pause.
  if (locked) {
    return <SubscriptionLockedNotice reason={reason} />;
  }

  function getPrettyExpiryDate() {
    // GraphQL's String scalar serializes Date fields via valueOf().toString(),
    // so showExpiresAt arrives as a ms-since-epoch string. Date.parse covers
    // the ISO/UTC case, parseInt covers the ms-string case.
    const expiresMs = showExpiresAt
      ? Date.parse(showExpiresAt) || parseInt(showExpiresAt, 10)
      : NaN;
    if (Number.isFinite(expiresMs)) {
      return getPrettyDate({
        date: String(Math.max(expiresMs, Date.now())),
        shortDate: true,
      });
    }
    return getPrettyDate({
      date: String(subscription?.current_period_end || ''),
      shortDate: true,
      isUnixTimestamp: true,
    });
  }

  return (
    <Box>
      <Text marginBottom="12px">Here&apos;s what will happen:</Text>
      <List.Root listStyleType="disc" marginLeft="20px" gap={2}>
        <List.Item>{`you will lose access to your show on ${getPrettyExpiryDate()}`}</List.Item>
        <List.Item>
          if you host with Alitu, your podcast will no longer be accessible by the date above
        </List.Item>
        <List.Item>files tied to this show will no longer be accessible</List.Item>
      </List.Root>
      <Text marginTop="12px">
        Feel free to{' '}
        <SupportLink message={`Hi, I'd like to remove my show. Can you help me with that?`}>
          contact our support team
        </SupportLink>{' '}
        if you have any further questions.
      </Text>
      <Button
        width="100%"
        marginTop="60px"
        visual="destructive"
        onClick={() => removeShow()}
        loading={loading}
      >
        Delete my show
        <RightArrow />
      </Button>
    </Box>
  );
}
